import React, { useRef, useEffect } from 'react';
import { X, Play, Pause, RotateCcw, Gamepad2, Signal } from 'lucide-react';
import { useSnakeGame } from './useSnakeGame';
import { SPEEDS, SpeedKey, CANVAS_WIDTH, CANVAS_HEIGHT } from './snakeConstants';
import styles from './SnakeGame.module.css';

interface SnakeGameProps {
    isOpen: boolean;
    onClose: () => void;
    backOnline?: boolean;
    onDismissBackOnline?: () => void;
}

const SnakeGame: React.FC<SnakeGameProps> = ({ isOpen, onClose, backOnline, onDismissBackOnline }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const modalRef  = useRef<HTMLDivElement>(null);

    const {
        score, highScore, gameState, speed,
        startGame, pauseGame, resumeGame, restartGame, setSpeed,
    } = useSnakeGame(canvasRef);

    // Focus modal on open so keyboard events fire
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => modalRef.current?.focus(), 50);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                ref={modalRef}
                className={styles.modal}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── HUD ── */}
                <div className={styles.hud}>
                    <div className={styles.hudStat}>
                        <span className={styles.statLabel}>SCORE</span>
                        <span className={styles.statValue}>{score}</span>
                    </div>

                    <div className={styles.hudCenter}>
                        <div className={styles.gameTitle}>
                            <Gamepad2 size={16} />
                            <span>Snake</span>
                        </div>
                        <div className={styles.speedGroup}>
                            {(Object.keys(SPEEDS) as SpeedKey[]).map((key) => (
                                <button
                                    key={key}
                                    className={`${styles.speedBtn} ${speed === key ? styles.speedActive : ''}`}
                                    onClick={() => setSpeed(key)}
                                    disabled={gameState === 'running'}
                                    title={`${SPEEDS[key].label} speed`}
                                >
                                    {SPEEDS[key].label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.hudStat} style={{ alignItems: 'flex-end' }}>
                        <span className={styles.statLabel}>BEST</span>
                        <span className={styles.statValue}>{highScore}</span>
                    </div>
                </div>

                {/* ── Canvas ── */}
                <div className={styles.canvasWrapper}>
                    <canvas
                        ref={canvasRef}
                        width={CANVAS_WIDTH}
                        height={CANVAS_HEIGHT}
                        className={styles.canvas}
                    />
                    {backOnline && (
                        <div className={styles.backOnlineBanner}>
                            <div className={styles.backOnlineIconWrap}>
                                <Signal size={32} color="#22c55e" />
                            </div>
                            <div className={styles.backOnlineText}>
                                <span className={styles.backOnlineTitle}>you're back online!</span>
                                <span className={styles.backOnlineSub}>time to get back to work...</span>
                            </div>
                            <div className={styles.backOnlineActions}>
                                <button className={styles.backOnlineBtnWork} onClick={onClose}>back to work</button>
                                <button className={styles.backOnlineBtnMore} onClick={onDismissBackOnline}>one more round</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Controls ── */}
                <div className={styles.controls}>
                    {gameState === 'idle' && (
                        <button className={styles.primaryBtn} onClick={startGame}>
                            <Play size={15} /> Start Game
                        </button>
                    )}
                    {gameState === 'running' && (
                        <button className={styles.secondaryBtn} onClick={pauseGame}>
                            <Pause size={15} /> Pause
                        </button>
                    )}
                    {gameState === 'paused' && (
                        <>
                            <button className={styles.primaryBtn} onClick={resumeGame}>
                                <Play size={15} /> Resume
                            </button>
                            <button className={styles.secondaryBtn} onClick={restartGame}>
                                <RotateCcw size={15} /> Restart
                            </button>
                        </>
                    )}
                    {gameState === 'dead' && (
                        <button className={styles.primaryBtn} onClick={restartGame}>
                            <RotateCcw size={15} /> Play Again
                        </button>
                    )}
                    <button className={styles.closeBtn} onClick={onClose}>
                        <X size={15} /> Close
                    </button>
                </div>

                {/* ── Hint ── */}
                <div className={styles.hint}>
                    Arrow keys / WASD to move &nbsp;·&nbsp; Space to pause &nbsp;·&nbsp; Esc to close
                </div>
            </div>
        </div>
    );
};

export default SnakeGame;
